import type { AddressSelection } from '@/services/equipment-intake.service';

/**
 * What a change of Received From does to everything hanging off it.
 *
 * Kept as a pure function, away from any screen, because this is the rule that
 * has been wrong in production: Return Location never followed Received From, so
 * every intake saved without touching it froze the customer's registered address
 * onto the dispatch note.
 *
 * THE LOCKED CHAIN:
 *
 *   Customer
 *     └── Received From        the operator states this
 *           ├── Contact        cleared — the person works at the old site
 *           └── Return         follows, unless deliberately overridden
 *     └── Invoice              NEVER follows. Billing is agreed with the
 *                              customer, not with the loading bay.
 */

export interface ReceivedFromState {
  locationId: string;
  contactId: string;
  returnTo: AddressSelection;
  /** Set once the operator picks a Return that is not the site. */
  returnOverridden: boolean;
}

export interface ReceivedFromChange {
  contactId: string;
  returnTo: AddressSelection;
  returnOverridden: boolean;
}

/**
 * Applies a new Received From.
 *
 * Note what is absent: this returns nothing about the invoice. It cannot change
 * it, because it never receives it. That is the enforcement — not a comment
 * asking a future maintainer to remember.
 */
export function applyReceivedFromChange(
  locationId: string,
  current: Pick<ReceivedFromState, 'returnTo' | 'returnOverridden'>,
): ReceivedFromChange {
  return {
    // A contact belongs to one location. Carrying it across would name someone
    // who does not work at the new site.
    contactId: '',
    // An override is a decision someone made on purpose. Correcting where the
    // equipment came from must not silently discard it.
    returnTo: current.returnOverridden ? current.returnTo : returnFollowing(locationId),
    returnOverridden: current.returnOverridden,
  };
}

/** The Return that corresponds to a site — the default relationship. */
export function returnFollowing(locationId: string): AddressSelection {
  return locationId
    ? { source: 'LOCATION', locationId }
    : // No site chosen yet: fall back to the customer's registered address so the
      // intake still resolves. Replaced the moment a site is picked.
      { source: 'CUSTOMER_DEFAULT' };
}

/** True when Return still matches its site — i.e. it is still following. */
export function isReturnFollowing(returnTo: AddressSelection, locationId: string): boolean {
  if (!locationId) return returnTo.source === 'CUSTOMER_DEFAULT';
  return returnTo.source === 'LOCATION' && returnTo.locationId === locationId;
}

/**
 * The operator picked a Return by hand. It stops following only if they actually
 * moved it off the site — re-picking the same place is not an override.
 */
export function applyReturnChange(
  next: AddressSelection,
  locationId: string,
): Pick<ReceivedFromState, 'returnTo' | 'returnOverridden'> {
  return { returnTo: next, returnOverridden: !isReturnFollowing(next, locationId) };
}

/** "Follow the site again" — drop the override and re-derive. */
export function resumeFollowing(
  locationId: string,
): Pick<ReceivedFromState, 'returnTo' | 'returnOverridden'> {
  return { returnTo: returnFollowing(locationId), returnOverridden: false };
}
